import React, { useState, useEffect, useRef } from 'react';
import { Canvas } from '@react-three/fiber';
import { Suspense } from 'react';
import LoadingScreen from './components/LoadingScreen';
import NavBar from './components/NavBar';
import MatrixBackground from './components/MatrixBackground';
import MatrixRain from './components/MatrixRain';
import CyberParticles from './components/CyberParticles';
import Hero from './components/Sections/Hero';
import About from './components/Sections/About';
import Skills from './components/Sections/Skills';
import Projects from './components/Sections/Projects';
import Achievements from './components/Sections/Achievements';
import Responsibility from './components/Sections/Responsibility';
import Contact from './components/Sections/Contact';
import './styles.css';

type SectionType = 'hero' | 'about' | 'skills' | 'projects' | 'achievements' | 'responsibility' | 'contact';

function App() {
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState<SectionType>('hero');
  const [showParticles, setShowParticles] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const transitionRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    // Simulate loading time
    const timer = setTimeout(() => {
      setLoading(false);
      
      // Add cyber particles after a short delay for smooth transition
      setTimeout(() => {
        setShowParticles(true);
      }, 1000);
    }, 3000);
    
    // Check for mobile view
    const checkMobileView = () => {
      setIsMobileView(window.innerWidth <= 576);
    };
    
    // Initial check
    checkMobileView();
    
    // Add resize listener
    window.addEventListener('resize', checkMobileView);
    
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', checkMobileView);
    };
  }, []);
  
  // Add CRT scan line and overlay effects
  useEffect(() => {
    if (!loading) {
      // Create scan line
      const scanLine = document.createElement('div');
      scanLine.className = 'cyber-scanline';
      document.body.appendChild(scanLine);
      
      // Create CRT overlay
      const crtOverlay = document.createElement('div');
      crtOverlay.className = 'crt-overlay';
      document.body.appendChild(crtOverlay);
      
      return () => {
        document.body.removeChild(scanLine);
        document.body.removeChild(crtOverlay);
      };
    }
  }, [loading]);
  
  const handleSectionChange = (section: SectionType) => {
    if (section === activeSection) return;
    
    // Trigger section transition animation
    setTransitioning(true);
    if (transitionRef.current) {
      transitionRef.current.style.top = `${window.scrollY}px`;
      transitionRef.current.classList.add('active');
    }
    
    // Change section after animation
    setTimeout(() => {
      setActiveSection(section);
      
      // Scroll to section
      const element = document.getElementById(section);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
      
      // End transition animation
      setTimeout(() => {
        if (transitionRef.current) {
          transitionRef.current.classList.remove('active');
        }
        setTransitioning(false);
      }, 500);
    }, 500);
  };
  
  if (loading) {
    return <LoadingScreen />;
  }
  
  return (
    <div className="app">
      {/* Section transition effect */}
      <div ref={transitionRef} className="section-transition" />
      
      {/* Matrix background */}
      <MatrixBackground 
        density={0.08}
        fontSize={14}
        speed={2}
        color="#00ff00"
        backgroundColor="rgba(0, 0, 0, 0.85)"
      />
      
      {/* Matrix code rain effect */}
      <MatrixRain 
        density={0.05} 
        speed={1.2} 
        fontSize={14} 
        opacity={0.7} 
        highlightProbability={0.1} 
      />
      
      {/* Cyber particle network effect */}
      {showParticles && <CyberParticles />}
      
      <div className="content-wrapper">
        <NavBar activeSection={activeSection} onSectionChange={handleSectionChange} />
        
        <main>
          <section id="hero">
            <Hero setActiveSection={handleSectionChange} />
          </section>
          
          <section id="about">
            <About />
          </section>
          
          <section id="skills">
            <Skills />
          </section>
          
          <section id="projects">
            <Projects />
          </section>
          
          <section id="achievements">
            <Achievements />
          </section>
          
          <section id="responsibility">
            <Responsibility />
          </section>
          
          <section id="contact">
            <Contact />
          </section>
        </main>
        
        <footer className="footer">
          <div className="footer-content">
            <div className="footer-text">
              © {new Date().getFullYear()} Aayush Kumar
            </div>
            
            <div className="footer-matrix-text">
              {isMobileView ? "[[MATRIX_CONNECTED]]" : "[[ACCESSING_NEURAL_NETWORK::MATRIX_CONNECTION_ESTABLISHED]]"}
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}

export default App;